import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { PremiumOverlay } from '../../components/ui/premium-overlay';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Spinner } from '../../components/ui/spinner';
import { useToast } from '../../hooks/use-toast';
import {
  ArrowLeft,
  Save,
  Upload,
  X,
  UserCircle,
  Trash2,
  CreditCard
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BD_PHONE_REGEX = /^01[3-9]\d{8}$/;

const ROLES = [
  { value: 'mechanic', en: 'Mechanic', bn: 'মেকানিক' },
  { value: 'helper', en: 'Helper', bn: 'হেল্পার' },
  { value: 'manager', en: 'Manager', bn: 'ম্যানেজার' },
  { value: 'electrician', en: 'Electrician', bn: 'ইলেকট্রিশিয়ান' },
  { value: 'painter', en: 'Painter', bn: 'পেইন্টার' },
  { value: 'cleaner', en: 'Cleaner', bn: 'ক্লিনার' },
  { value: 'accountant', en: 'Accountant', bn: 'হিসাবরক্ষক' },
  { value: 'other', en: 'Other', bn: 'অন্যান্য' },
];

export default function EmployeeEditPage() {
  const { id, garageSlug } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { isPremium } = useAuth();
  const { toast } = useToast();
  const basePath = garageSlug ? `/${garageSlug}/admin` : '/admin';
  const fileRef = useRef();
  const nidFrontRef = useRef();
  const nidBackRef = useRef();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    role: 'mechanic',
    nid: '',
    nid_front: null,
    nid_back: null,
    address: '',
    join_date: '',
    monthly_salary: '',
    emergency_contact: '',
    emergency_phone: '',
    notes: '',
    is_active: true,
    photo: null,
  });

  useEffect(() => {
    fetchEmployee();
  }, [id]);

  const fetchEmployee = async () => {
    try {
      const res = await axios.get(`${API}/employees/${id}`);
      const e = res.data;
      setForm({
        name: e.name || '',
        phone: e.phone || '',
        role: e.role || 'mechanic',
        nid: e.nid || '',
        nid_front: e.nid_front || null,
        nid_back: e.nid_back || null,
        address: e.address || '',
        join_date: e.join_date || '',
        monthly_salary: e.monthly_salary || '',
        emergency_contact: e.emergency_contact || '',
        emergency_phone: e.emergency_phone || '',
        notes: e.notes || '',
        is_active: e.is_active !== false,
        photo: e.photo || null,
      });
    } catch (err) {
      console.error('Failed to fetch employee:', err);
      navigate(`${basePath}/employees`);
    } finally {
      setLoading(false);
    }
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: language === 'en' ? 'File too large (max 5MB)' : 'ফাইল খুব বড় (সর্বোচ্চ ৫MB)', variant: 'destructive' });
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setForm(f => ({ ...f, photo: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const handleNidPhoto = (field, ref) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: language === 'en' ? 'Photo too large (max 5MB)' : 'ছবি অনেক বড় (সর্বোচ্চ ৫MB)', variant: 'destructive' });
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setForm(f => ({ ...f, [field]: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast({ title: language === 'en' ? 'Name and phone are required' : 'নাম ও ফোন আবশ্যক', variant: 'destructive' });
      return;
    }
    if (!BD_PHONE_REGEX.test(form.phone.trim())) {
      toast({ title: language === 'en' ? 'Invalid BD phone number (01XXXXXXXXX)' : 'ভুল ফোন নম্বর (01XXXXXXXXX)', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/employees/${id}`, {
        ...form,
        monthly_salary: parseFloat(form.monthly_salary) || 0,
      });
      toast({ title: language === 'en' ? 'Employee updated!' : 'কর্মচারী আপডেট হয়েছে!' });
      navigate(`${basePath}/employees/${id}`);
    } catch (err) {
      toast({ title: language === 'en' ? 'Failed to update' : 'আপডেট ব্যর্থ হয়েছে', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(language === 'en' ? 'Are you sure you want to delete this employee?' : 'আপনি কি নিশ্চিত এই কর্মচারী মুছে ফেলতে চান?')) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/employees/${id}`);
      toast({ title: language === 'en' ? 'Employee deleted' : 'কর্মচারী মুছে ফেলা হয়েছে' });
      navigate(`${basePath}/employees`);
    } catch (err) {
      toast({ title: language === 'en' ? 'Failed to delete' : 'মুছে ফেলা ব্যর্থ হয়েছে', variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  };

  if (loading && isPremium) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <PremiumOverlay feature={language === 'en' ? 'Employee & Payroll Management' : 'কর্মচারী ও বেতন ব্যবস্থাপনা'} locked={!isPremium}>
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate(`${basePath}/employees/${id}`)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-bold font-['Barlow_Condensed']">
            {language === 'en' ? 'Edit Employee' : 'কর্মচারী সম্পাদনা'}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Photo */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                {form.photo ? (
                  <div className="relative">
                    <img src={form.photo} alt="Photo" className="w-20 h-20 rounded-full object-cover" />
                    <button type="button" onClick={() => setForm(f => ({ ...f, photo: null }))} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center">
                    <UserCircle className="w-10 h-10 text-slate-400" />
                  </div>
                )}
                <div>
                  <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={handleFile} />
                  <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                    <Upload className="w-4 h-4 mr-2" />
                    {language === 'en' ? 'Change Photo' : 'ছবি পরিবর্তন'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Basic Info */}
          <Card>
            <CardHeader><CardTitle>{language === 'en' ? 'Basic Information' : 'মৌলিক তথ্য'}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{language === 'en' ? 'Full Name' : 'পুরো নাম'} <span className="text-red-500">*</span></Label>
                  <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
                </div>
                <div>
                  <Label>{language === 'en' ? 'Phone' : 'ফোন'} <span className="text-red-500">*</span></Label>
                  <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} required maxLength={11} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{language === 'en' ? 'Role / Designation' : 'পদবি'}</Label>
                  <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r[language]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{language === 'en' ? 'NID Number' : 'NID নম্বর'}</Label>
                  <Input value={form.nid} onChange={e => setForm(f => ({ ...f, nid: e.target.value }))} />
                </div>
              </div>

              {/* NID Photos */}
              <div>
                <Label className="flex items-center gap-2 mb-2"><CreditCard className="w-4 h-4" />{language === 'en' ? 'NID Photos' : 'NID ছবি'}</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-3 text-center">
                    {form.nid_front ? (
                      <div className="relative">
                        <img src={form.nid_front} alt="NID Front" className="w-full h-28 object-contain rounded" />
                        <button type="button" onClick={() => { setForm(f => ({ ...f, nid_front: null })); if (nidFrontRef.current) nidFrontRef.current.value = ''; }} className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
                      </div>
                    ) : (
                      <div>
                        <input ref={nidFrontRef} type="file" accept="image/*" className="hidden" onChange={handleNidPhoto('nid_front', nidFrontRef)} />
                        <Button type="button" variant="ghost" size="sm" onClick={() => nidFrontRef.current?.click()} className="text-slate-400">
                          <Upload className="w-4 h-4 mr-1" />{language === 'en' ? 'Front Side' : 'সামনের দিক'}
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-3 text-center">
                    {form.nid_back ? (
                      <div className="relative">
                        <img src={form.nid_back} alt="NID Back" className="w-full h-28 object-contain rounded" />
                        <button type="button" onClick={() => { setForm(f => ({ ...f, nid_back: null })); if (nidBackRef.current) nidBackRef.current.value = ''; }} className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
                      </div>
                    ) : (
                      <div>
                        <input ref={nidBackRef} type="file" accept="image/*" className="hidden" onChange={handleNidPhoto('nid_back', nidBackRef)} />
                        <Button type="button" variant="ghost" size="sm" onClick={() => nidBackRef.current?.click()} className="text-slate-400">
                          <Upload className="w-4 h-4 mr-1" />{language === 'en' ? 'Back Side' : 'পিছনের দিক'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <Label>{language === 'en' ? 'Address' : 'ঠিকানা'}</Label>
                <Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{language === 'en' ? 'Join Date' : 'যোগদানের তারিখ'}</Label>
                  <Input type="date" value={form.join_date} onChange={e => setForm(f => ({ ...f, join_date: e.target.value }))} />
                </div>
                <div>
                  <Label>{language === 'en' ? 'Monthly Salary (৳)' : 'মাসিক বেতন (৳)'}</Label>
                  <Input type="number" min="0" value={form.monthly_salary} onChange={e => setForm(f => ({ ...f, monthly_salary: e.target.value }))} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Emergency & Notes */}
          <Card>
            <CardHeader><CardTitle>{language === 'en' ? 'Emergency & Notes' : 'জরুরি ও নোট'}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{language === 'en' ? 'Emergency Contact Name' : 'জরুরি যোগাযোগের নাম'}</Label>
                  <Input value={form.emergency_contact} onChange={e => setForm(f => ({ ...f, emergency_contact: e.target.value }))} />
                </div>
                <div>
                  <Label>{language === 'en' ? 'Emergency Phone' : 'জরুরি ফোন'}</Label>
                  <Input value={form.emergency_phone} onChange={e => setForm(f => ({ ...f, emergency_phone: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label>{language === 'en' ? 'Notes' : 'নোট'}</Label>
                <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
            </CardContent>
          </Card>

          {/* Active Toggle */}
          <Card>
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <p className="font-medium">{language === 'en' ? 'Active Status' : 'সক্রিয় অবস্থা'}</p>
                <p className="text-sm text-slate-500">{language === 'en' ? 'Inactive employees are hidden from the main list' : 'নিষ্ক্রিয় কর্মচারী তালিকায় দেখা যাবে না'}</p>
              </div>
              <Switch checked={form.is_active} onCheckedChange={v => setForm(f => ({ ...f, is_active: v }))} />
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <Button type="button" variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? <Spinner size="sm" className="mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              {language === 'en' ? 'Delete' : 'মুছুন'}
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => navigate(`${basePath}/employees/${id}`)}>
                {language === 'en' ? 'Cancel' : 'বাতিল'}
              </Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving ? <Spinner size="sm" className="mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                {language === 'en' ? 'Save Changes' : 'সংরক্ষণ'}
              </Button>
            </div>
          </div>
        </form>
      </div>
      </PremiumOverlay>
    </AdminLayout>
  );
}
